import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Loader2, Mail, Plus, Search, ShieldCheck, UserCog, Users2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { adminApi } from "@/lib/api";

export const Route = createFileRoute("/admin/staff")({
  component: StaffPage,
});

type AdminUser = {
  id: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  createdAt: string;
  roles: string[];
};

type StaffDraft = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: "Moderator" | "Support";
  password: string;
};

const emptyDraft: StaffDraft = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  role: "Moderator",
  password: "",
};

function StaffPage() {
  const [staff, setStaff] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [draft, setDraft] = useState<StaffDraft>(emptyDraft);

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      adminApi.users({ role: "Moderator", pageSize: 100 }),
      adminApi.users({ role: "Support", pageSize: 100 }),
    ])
      .then(([moderators, support]) => {
        const byId = new Map<string, AdminUser>();
        [...moderators.data, ...support.data].forEach(user => byId.set(user.id, user));
        setStaff([...byId.values()].sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)));
      })
      .catch(err => setError(err instanceof Error ? err.message : "Failed to load staff users"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return staff;
    return staff.filter(user =>
      `${user.firstName ?? ""} ${user.lastName ?? ""} ${user.email ?? ""} ${user.phone ?? ""} ${user.roles.join(" ")}`
        .toLowerCase()
        .includes(needle),
    );
  }, [staff, search]);

  const counts = useMemo(() => ({
    total: staff.length,
    moderators: staff.filter(user => user.roles.includes("Moderator")).length,
    support: staff.filter(user => user.roles.includes("Support")).length,
  }), [staff]);

  const updateDraft = <K extends keyof StaffDraft>(key: K, value: StaffDraft[K]) => {
    setDraft(current => ({ ...current, [key]: value }));
  };

  const handleCreate = () => {
    toast.error("Staff account creation needs a backend endpoint before it can be submitted.");
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 sm:p-6 lg:p-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-primary">Super Admin</p>
          <h1 className="font-display text-2xl font-bold tracking-tight">Staff / Admin Users</h1>
          <p className="mt-1 text-sm text-muted-foreground">Manage moderator and support access for the platform.</p>
        </div>
        <Button className="gap-2" onClick={() => setCreateOpen(true)}>
          <Plus className="size-4" /> Add staff
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        {[
          { label: "Staff users", value: counts.total, icon: Users2 },
          { label: "Moderators", value: counts.moderators, icon: ShieldCheck },
          { label: "Support", value: counts.support, icon: UserCog },
        ].map(item => (
          <div key={item.label} className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft">
            <div className="flex items-start justify-between">
              <div className="grid size-10 place-items-center rounded-lg bg-accent text-primary">
                <item.icon className="size-5" />
              </div>
            </div>
            <div className="mt-4 font-[family-name:Inter] text-2xl font-bold">{loading ? "..." : item.value}</div>
            <div className="text-xs text-muted-foreground">{item.label}</div>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-border/60 bg-card shadow-soft">
        <div className="flex flex-col gap-3 border-b border-border/60 p-4 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="font-display text-lg font-semibold">Admin staff</h2>
          <div className="relative sm:w-72">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Search staff"
              className="pl-9"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Loading staff users...
          </div>
        ) : error ? (
          <div className="p-8 text-center text-sm text-destructive">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center">
            <Users2 className="mx-auto mb-3 size-10 text-muted-foreground/30" />
            <p className="font-semibold">No staff users found</p>
            <p className="mt-1 text-sm text-muted-foreground">Try a different search term.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/60 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/40">
                {filtered.map(user => (
                  <tr key={user.id} className="hover:bg-accent/30">
                    <td className="px-4 py-3 font-medium">
                      {[user.firstName, user.lastName].filter(Boolean).join(" ") || "Unnamed staff"}
                      {user.phone && <div className="text-xs font-normal text-muted-foreground">{user.phone}</div>}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      <span className="inline-flex items-center gap-1">
                        <Mail className="size-3.5" /> {user.email}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {user.roles.map(role => (
                          <Badge key={role} variant="outline" className="rounded-full">{role}</Badge>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {new Date(user.createdAt).toLocaleDateString("en-NG", { day: "2-digit", month: "short", year: "numeric" })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add staff member</DialogTitle>
            <DialogDescription>
              Prepare moderator or support account details.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="staff-first-name">First name</Label>
              <Input id="staff-first-name" value={draft.firstName} onChange={event => updateDraft("firstName", event.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="staff-last-name">Last name</Label>
              <Input id="staff-last-name" value={draft.lastName} onChange={event => updateDraft("lastName", event.target.value)} />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="staff-email">Email</Label>
              <Input id="staff-email" type="email" value={draft.email} onChange={event => updateDraft("email", event.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="staff-phone">Phone</Label>
              <Input id="staff-phone" value={draft.phone} onChange={event => updateDraft("phone", event.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="staff-role">Role</Label>
              <select
                id="staff-role"
                value={draft.role}
                onChange={event => updateDraft("role", event.target.value as StaffDraft["role"])}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
              >
                <option value="Moderator">Moderator</option>
                <option value="Support">Support</option>
              </select>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="staff-password">Temporary password</Label>
              <Input id="staff-password" type="password" value={draft.password} onChange={event => updateDraft("password", event.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate}>Create staff account</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
